import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { parseInputTemplate } from './BatchJobSettings';

export interface StepInputFieldMeta {
  name: string;
  type?: string;
  required?: boolean;
  description?: string | null;
}

interface StepInputFieldsProps {
  value: unknown;
  onChange: (next: Record<string, any>) => void;
  fields?: StepInputFieldMeta[];
  /** Keys handled by another control (e.g. batch model settings). */
  hideKeys?: string[];
  size?: 'small' | 'medium';
  /** Collapsed JSON of the inputTemplate object. Off when a parent already has one. */
  showJsonEditor?: boolean;
}

const TEMPLATE_REF = /\{\{/;

function baseType(t?: string): string {
  return (t || '').replace(/<.*>$/, '').toLowerCase();
}

function displayValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function coerceValue(raw: string, type?: string): any {
  const t = raw.trim();
  if (t === '') return undefined;
  if (TEMPLATE_REF.test(t)) return t;
  const kind = baseType(type);
  if (kind === 'boolean') {
    const low = t.toLowerCase();
    if (low === 'true' || low === '1' || low === 'yes') return true;
    if (low === 'false' || low === '0' || low === 'no') return false;
    return t;
  }
  if (kind === 'number' || kind === 'integer') {
    // Keep incomplete in-progress values as strings ("0.", "-") so typing 0.4 works.
    if (!/^-?\d+(\.\d+)?$/.test(t)) return t;
    const n = Number(t);
    if (Number.isFinite(n)) return kind === 'integer' ? Math.trunc(n) : n;
    return t;
  }
  if (kind === 'array' || kind === 'object' || t.startsWith('[') || t.startsWith('{')) {
    try {
      return JSON.parse(t);
    } catch {
      if (kind === 'array' && !t.startsWith('[')) {
        return t.split(',').map((s) => s.trim()).filter(Boolean);
      }
      return t;
    }
  }
  return raw;
}

function setKey(obj: Record<string, any>, key: string, next: any): Record<string, any> {
  const out = { ...obj };
  if (next === undefined) delete out[key];
  else out[key] = next;
  return out;
}

function prettyJson(obj: Record<string, any>): string {
  return Object.keys(obj).length ? JSON.stringify(obj, null, 2) : '{\n}';
}

const HOW_IT_WORKS_TOOLTIP = (
  <Box sx={{ '& p': { m: 0, mb: 0.75, fontSize: '0.75rem', lineHeight: 1.45 }, '& p:last-child': { mb: 0 } }}>
    <p>
      <strong>This JSON is what THIS step receives when it runs</strong> — it is not
      sent onward to the next step.
    </p>
    <p>
      Three placeholder families (they are not interchangeable):
    </p>
    <p>
      <code>{'{{item.original}}'}</code> — the current row, only when Map Array is
      on. The array itself is chosen above (e.g. Array Field <code>node-4.items</code>).
    </p>
    <p>
      <code>{'{{node-1.items}}'}</code> — a field from a previous step&apos;s output
      (the whole list, once). <code>{'{{node-1}}'}</code> is that step&apos;s entire output.
    </p>
    <p>
      <code>{'{{input.grocery_input}}'}</code> — what the user typed in the run dialog.
      Literals have no braces (e.g. <code>0.4</code>).
    </p>
  </Box>
);

/**
 * The step's inputTemplate: required wiring plus any overrides actually set
 * on this step. Unused plugin defaults stay off-screen (add them from the
 * picker, or edit the JSON). Empty optional keys are omitted so the plugin
 * default still applies.
 */
export const StepInputFields: React.FC<StepInputFieldsProps> = ({
  value,
  onChange,
  fields = [],
  hideKeys = [],
  size = 'small',
  showJsonEditor = true,
}) => {
  const tpl = parseInputTemplate(value);
  const hidden = useMemo(() => new Set(hideKeys), [hideKeys]);
  const [newKey, setNewKey] = useState('');
  const [jsonOpen, setJsonOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [jsonDraft, setJsonDraft] = useState(() => prettyJson(parseInputTemplate(value)));
  const [jsonError, setJsonError] = useState('');

  const metaByName = useMemo(() => {
    const m = new Map<string, StepInputFieldMeta>();
    for (const f of fields) {
      if (f?.name) m.set(f.name, f);
    }
    return m;
  }, [fields]);

  const tplKey = JSON.stringify(tpl);
  useEffect(() => {
    try {
      const parsed = JSON.parse(jsonDraft || '{}');
      if (JSON.stringify(parsed) === tplKey) return;
    } catch {
      return;
    }
    setJsonDraft(prettyJson(JSON.parse(tplKey)));
    setJsonError('');
  }, [tplKey]); // eslint-disable-line react-hooks/exhaustive-deps -- draft must not reset mid-invalid-edit

  const visibleNames = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    const add = (n: string) => {
      if (!n || hidden.has(n) || seen.has(n)) return;
      seen.add(n);
      ordered.push(n);
    };
    for (const f of fields) {
      if (f.required) add(f.name);
    }
    for (const k of Object.keys(tpl)) add(k);
    return ordered;
  }, [fields, tpl, hidden]);

  const unusedPluginKeys = fields.filter(
    (f) => f?.name && !hidden.has(f.name) && !Object.prototype.hasOwnProperty.call(tpl, f.name) && !f.required,
  );

  const defaultFor = (name: string) => {
    const kind = baseType(metaByName.get(name)?.type);
    if (kind === 'boolean') return true;
    if (kind === 'number' || kind === 'integer') return '';
    return '';
  };

  const renderField = (name: string) => {
    const meta = metaByName.get(name);
    const kind = baseType(meta?.type);
    const current = Object.prototype.hasOwnProperty.call(tpl, name) ? tpl[name] : undefined;
    const set = current !== undefined;
    const label = name.replace(/_/g, ' ');

    if (kind === 'boolean') {
      const selectVal = current === false ? 'false' : current === true ? 'true' : '';
      return (
        <Box key={name} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <FormControl fullWidth size={size}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.25 }}>
              {label}
            </Typography>
            <Select
              value={selectVal}
              displayEmpty
              renderValue={(v) => (v === 'false' ? 'No' : v === 'true' ? 'Yes' : 'Choose…')}
              onChange={(e) => {
                const v = e.target.value as string;
                if (v !== 'true' && v !== 'false') return;
                onChange(setKey(tpl, name, v === 'true'));
              }}
            >
              {selectVal === '' && <MenuItem value="">Choose…</MenuItem>}
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
            {meta?.description && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', lineHeight: 1.35 }}>
                {meta.description}
              </Typography>
            )}
          </FormControl>
          {set && !meta?.required && (
            <Tooltip title="Clear (use plugin default)">
              <IconButton size="small" onClick={() => onChange(setKey(tpl, name, undefined))} sx={{ mt: 2 }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }

    const multiline = kind === 'array' || kind === 'object' || (typeof current === 'object' && current !== null);
    return (
      <Box key={name} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size={size}
          label={label}
          value={displayValue(current)}
          onChange={(e) => {
            const next = coerceValue(e.target.value, meta?.type);
            onChange(setKey(tpl, name, next === undefined && meta?.required ? '' : next));
          }}
          placeholder={meta?.required ? 'e.g. {{node-1.items}}' : undefined}
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          helperText={
            meta?.description
              ? `${meta.required ? 'Required. ' : ''}${meta.description}`
              : undefined
          }
        />
        {set && !meta?.required && (
          <Tooltip title="Clear (use plugin default)">
            <IconButton
              size="small"
              onClick={() => onChange(setKey(tpl, name, undefined))}
              sx={{ mt: 0.5 }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const addCustom = () => {
    const key = newKey.trim();
    if (!key || hidden.has(key) || Object.prototype.hasOwnProperty.call(tpl, key)) {
      setNewKey('');
      return;
    }
    onChange(setKey(tpl, key, defaultFor(key)));
    setNewKey('');
  };

  const applyJson = (raw: string) => {
    setJsonDraft(raw);
    const t = raw.trim();
    if (!t) {
      setJsonError('');
      onChange({});
      return;
    }
    try {
      const parsed = JSON.parse(t);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError('Must be a JSON object');
        return;
      }
      setJsonError('');
      onChange(parsed);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.25 }}>
        <Typography variant="subtitle2">Step inputs</Typography>
        <Tooltip
          arrow
          enterDelay={200}
          placement="top"
          title={HOW_IT_WORKS_TOOLTIP}
          slotProps={{ tooltip: { sx: { maxWidth: 420, p: 1.5 } } }}
        >
          <IconButton size="small" aria-label="How step inputs work" sx={{ p: 0.25 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.4 }}>
        Values this step receives when it runs. Add keys the AI missed — you do not
        need to re-run the builder.
      </Typography>
      <Button
        size="small"
        onClick={() => setHelpOpen((o) => !o)}
        endIcon={helpOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none', px: 0, mb: 1, minHeight: 0 }}
      >
        {helpOpen ? 'Hide how this works' : 'How this works'}
      </Button>
      <Collapse in={helpOpen}>
        <Box
          sx={{
            mb: 1.5,
            px: 1.25,
            py: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            '& p': { m: 0, mb: 0.75, fontSize: '0.75rem', lineHeight: 1.45, color: 'text.secondary' },
            '& p:last-child': { mb: 0 },
            '& code': { fontSize: '0.7rem' },
          }}
        >
          <Typography component="p">
            Each key below is sent into <strong>this</strong> plugin or function as its
            input. It does not automatically become the next step&apos;s input — that
            step has its own mapping. You can mix placeholders and literals in the
            same object.
          </Typography>
          <Typography component="p">
            <strong>Map Array + {'{{item…}}'}:</strong> Map Array (above) means this
            step runs once per row. Array Field names the list (e.g.{' '}
            <code>node-4.items</code>). Inside the template, <code>item</code> is that
            row: <code>{'{{item.original}}'}</code>, <code>{'{{item.candidates}}'}</code>.
            Use this when the step should see one grocery item, not the whole array.
          </Typography>
          <Typography component="p">
            <strong>{'{{node-N…}}'} (whole previous output):</strong>{' '}
            <code>{'{{node-1.items}}'}</code> is the <code>items</code> field from step
            1 — the whole array, in one shot. <code>{'{{node-1}}'}</code> is that
            step&apos;s entire output. The search plugin uses this because it loops
            internally. A ranker that maps over items uses <code>item</code> instead.
          </Typography>
          <Typography component="p">
            <strong>{'{{input…}}'}:</strong>{' '}
            <code>{'{{input.grocery_input}}'}</code> is what the user typed in the run
            dialog (the name after <code>input.</code> is the runtime field key).
          </Typography>
          <Typography component="p">
            <strong>Literals</strong> (no braces): <code>0.4</code>, <code>true</code>,
            a catalog hash. Leave a plugin option off this object to keep its code
            default. Add keys the AI missed with the picker or &quot;add any key.&quot;
          </Typography>
        </Box>
      </Collapse>

      {visibleNames.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visibleNames.map(renderField)}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Nothing pinned yet. Pass-through uses the previous step unless you add a mapping.
        </Typography>
      )}

      {unusedPluginKeys.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Add a plugin input the builder left off
          </Typography>
          <FormControl fullWidth size={size}>
            <Select
              value=""
              displayEmpty
              onChange={(e) => {
                const k = String(e.target.value || '');
                if (!k) return;
                onChange(setKey(tpl, k, defaultFor(k)));
              }}
              renderValue={() => (
                <Typography variant="body2" color="text.secondary">
                  Choose a known input to pin…
                </Typography>
              )}
            >
              <MenuItem value="" disabled sx={{ display: 'none' }}>
                Choose a known input to pin…
              </MenuItem>
              {unusedPluginKeys.map((f) => (
                <MenuItem key={f.name} value={f.name}>
                  <Box>
                    <Typography variant="body2">{f.name.replace(/_/g, ' ')}</Typography>
                    {f.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, whiteSpace: 'normal' }}>
                        {f.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'flex-start' }}>
        <TextField
          size={size}
          label="Or add any key"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addCustom(); }}
          placeholder="e.g. customer_hash"
          helperText="Works even if this key was not in the builder prompt."
          sx={{ flex: 1 }}
        />
        <Button size="small" startIcon={<AddIcon />} onClick={addCustom} sx={{ textTransform: 'none', mt: 0.5 }}>
          Add
        </Button>
      </Box>

      {showJsonEditor && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setJsonOpen((o) => !o)}
            endIcon={jsonOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', px: 0 }}
          >
            {jsonOpen ? 'Hide JSON' : 'View / edit JSON'}
          </Button>
          <Collapse in={jsonOpen}>
            <TextField
              fullWidth
              size={size}
              multiline
              minRows={6}
              value={jsonDraft}
              onChange={(e) => applyJson(e.target.value)}
              error={!!jsonError}
              helperText={jsonError || 'Edit the same object as the fields above. Placeholders stay quoted as strings, e.g. "items": "{{node-1.items}}".'}
              sx={{ mt: 1, '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default StepInputFields;
